import { Controller } from "@hotwired/stimulus"
import { Chart } from 'chart.js/auto'

export default class extends Controller {
  static values = {
    url: String,
    format: { type: String, default: "dollars" },
    type: { type: String, default: "line" }
  }

  connect() {
    this.themeChangedHandler = () => this.applyThemeColors()
    document.addEventListener("theme:changed", this.themeChangedHandler)
    this.fetchData()
  }

  isDark() {
    return document.documentElement.classList.contains("dark")
  }

  // "2026-05" -> "May 2026" (short month + year)
  monthLabel(ym) {
    if (!ym) return ym
    const [year, month] = ym.split('-')
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
    return `${months[parseInt(month) - 1]} ${year}`
  }

  themeColors() {
    return this.isDark()
      ? { tick: "rgba(229, 231, 235, 0.85)", grid: "rgba(255, 255, 255, 0.08)" }
      : { tick: "rgba(55, 65, 81, 0.85)", grid: "rgba(0, 0, 0, 0.08)" }
  }

  applyThemeColors() {
    if (!this.chart) return
    const { tick, grid } = this.themeColors()
    this.chart.options.scales.x.ticks.color = tick
    this.chart.options.scales.y.ticks.color = tick
    this.chart.options.scales.x.grid = { ...(this.chart.options.scales.x.grid || {}), color: grid }
    this.chart.options.scales.y.grid = { ...(this.chart.options.scales.y.grid || {}), color: grid }
    this.chart.update()
  }

  async fetchData() {
    try {
      const response = await fetch(this.urlValue)
      const data = await response.json()
      this.initializeChart(data)
    } catch (error) {
      console.error('Error fetching billing data:', error)
    }
  }

  initializeChart(data) {
    const ctx = this.element.getContext('2d')
    const labels = data.map(d => this.monthLabel(d.month))
    const values = data.map(d => d.total)
    const format = data.length > 0 && data[0].format ? data[0].format : this.formatValue
    const { tick, grid } = this.themeColors()

    this.chart = new Chart(ctx, {
      type: this.typeValue,
      data: {
        labels: labels,
        datasets: [{
          label: format === 'dollars' ? 'Total Billed' : 'Total Hours',
          data: values,
          tension: 0.4,
          pointBackgroundColor: 'rgb(79, 70, 229)',
          pointRadius: 4,
          backgroundColor: 'rgb(79, 70, 229)',
          borderColor: 'rgb(79, 70, 229)',
          borderWidth: 2,
          fill: false,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(tooltipItem) {
                if (format === 'dollars') {
                  return '$' + tooltipItem.formattedValue
                }
                return tooltipItem.formattedValue + ' hrs'
              }
            }
          },
        },
        scales: {
          y: {
            ticks: {
              color: tick,
              callback: function(value) {
                if (format === 'dollars') {
                  return '$' + value
                }
                return value + 'h'
              }
            },
            grid: { color: grid }
          },
          x: {
            ticks: { color: tick },
            grid: { color: grid }
          }
        }
      }
    })
  }

  disconnect() {
    if (this.themeChangedHandler) {
      document.removeEventListener("theme:changed", this.themeChangedHandler)
    }
    if (this.chart) {
      this.chart.destroy()
    }
  }
}
